import React from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  ShoppingBag,
  HelpCircle,
  PhoneForwarded,
  Phone,
  CheckCircle2,
  Languages,
  Zap,
} from 'lucide-react';
import { TEAM_AVATARS } from '../teamAvatars';

/* Rachel — massive visual presence for the AI Receptionist landing.
   Dark bento: giant avatar + live call transcript + capability tiles. */

const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');

const Grow = ({ children, i = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function RachelShowcase({ lang }) {
  const es = lang === 'es';

  const caps = [
    {
      icon: Calendar,
      t: es ? 'Reservas' : 'Reservations',
      b: es
        ? 'Toma, confirma y modifica reservas — integrada con OpenTable o tu sistema directo.'
        : 'Takes, confirms and modifies reservations — integrated with OpenTable or your direct system.',
    },
    {
      icon: ShoppingBag,
      t: es ? 'Pedidos por teléfono' : 'Phone orders',
      b: es
        ? 'Captura pedidos telefónicos y los envía directo a tu POS, sin errores de transcripción.'
        : 'Captures phone orders and sends them straight into your POS — no transcription errors.',
    },
    {
      icon: HelpCircle,
      t: es ? 'Preguntas frecuentes' : 'Guest FAQs',
      b: es
        ? 'Horarios, menú, estacionamiento, alérgenos, eventos — responde al instante con tu información real.'
        : 'Hours, menu, parking, allergens, events — answered instantly with your real information.',
    },
    {
      icon: PhoneForwarded,
      t: es ? 'Escalado a humano' : 'Human handoff',
      b: es
        ? 'Quejas o casos sensibles se transfieren a tu equipo en segundos, con el contexto completo.'
        : 'Complaints or sensitive cases transfer to your team in seconds — with full context.',
    },
  ];

  const stats = [
    { k: '100%', v: es ? 'Llamadas contestadas' : 'Calls answered' },
    { k: '< 2s', v: es ? 'En atender cada llamada' : 'To pick up every call' },
    { k: '+30%', v: es ? 'Reservas capturadas' : 'Reservations captured' },
    { k: '24/7', v: es ? 'Sin descansos ni festivos' : 'No breaks, no holidays' },
  ];

  const transcript = [
    {
      who: 'guest',
      msg: es
        ? '"Hola, ¿tienen mesa para 4 este sábado a las 8?"'
        : '"Hi, do you have a table for 4 this Saturday at 8?"',
    },
    {
      who: 'rachel',
      msg: es
        ? '"¡Con gusto! A las 8:00 estamos completos, pero tengo 7:45 o 8:30 en terraza. ¿Cuál prefieres?"'
        : '"Happy to help! 8:00 is fully booked, but I have 7:45 or 8:30 on the terrace. Which do you prefer?"',
    },
    {
      who: 'guest',
      msg: es ? '"7:45 está perfecto. A nombre de Laura."' : '"7:45 is perfect. Under Laura."',
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid="showcase-ai-receptionist">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-[15%] w-[600px] h-[420px] rounded-full blur-3xl opacity-[0.13]"
        style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
      />
      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="kx-eyebrow-dark">{es ? 'Conoce a tu recepcionista' : 'Meet your receptionist'}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">
            {es ? 'Ella es Rachel. Nunca deja sonar tu teléfono.' : 'This is Rachel. She never lets your phone ring out.'}
          </h2>
          <p className="mt-4 text-neutral-400 text-[15.5px] leading-relaxed">
            {es
              ? 'Rachel es la empleada IA de Kitxens entrenada con tu menú, tus horarios y tus políticas. Contesta cada llamada con la voz de tu marca — mientras tu equipo atiende a los clientes que están en la mesa.'
              : "Rachel is the Kitxens AI employee trained on your menu, hours and policies. She answers every call in your brand voice — while your team takes care of the guests at the table."}
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Rachel — massive presence */}
          <Grow i={0} className="lg:col-span-5">
            <div className="relative h-full rounded-3xl bg-gradient-to-b from-[#F77F11]/[0.14] to-white/[0.03] border border-[#F77F11]/25 p-7 flex flex-col items-center text-center overflow-hidden">
              <div
                aria-hidden="true"
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-[340px] h-[340px] rounded-full opacity-40 pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(247,127,17,0.35), transparent 70%)' }}
              />
              <div className="relative rounded-full p-[4px] bg-gradient-to-br from-[#F77F11] to-[#ff9b4a] shadow-[0_25px_70px_-20px_rgba(247,127,17,0.7)]">
                <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-full overflow-hidden bg-neutral-900">
                  <img src={rachel.img} alt="Rachel — AI Receptionist" className="w-full h-full object-cover" />
                </div>
                <span className="absolute bottom-2 right-2 flex w-6 h-6">
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
                  <span className="relative w-6 h-6 rounded-full bg-emerald-400 ring-4 ring-neutral-950 flex items-center justify-center">
                    <Phone className="w-3 h-3 text-neutral-950" />
                  </span>
                </span>
              </div>
              <p className="relative mt-6 text-2xl font-bold tracking-[-0.01em]">Rachel</p>
              <p className="relative text-[12px] tracking-[0.2em] uppercase font-bold text-[#FFB672]">AI Receptionist</p>
              <div className="relative mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {es ? 'En llamada ahora' : 'On a call now'}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/15 text-neutral-300 text-[11px] font-semibold px-3 py-1">
                  <Languages className="w-3 h-3" /> EN · ES
                </span>
              </div>
              <p className="relative mt-5 text-[13px] text-neutral-400 leading-relaxed max-w-xs">
                {es
                  ? 'Entrenada con tu menú y tus políticas. Habla con la voz de tu marca, no como un robot.'
                  : 'Trained on your menu and policies. She speaks in your brand voice — not like a robot.'}
              </p>
            </div>
          </Grow>

          {/* Live call transcript */}
          <Grow i={1} className="lg:col-span-7">
            <div className="h-full rounded-3xl bg-white/[0.04] border border-white/10 p-6 sm:p-7 flex flex-col">
              <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-neutral-500">
                  {es ? 'Llamada en vivo · Sábado 6:12 PM' : 'Live call · Saturday 6:12 PM'}
                </p>
                <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                </span>
              </div>
              <div className="mt-5 space-y-3 flex-1">
                {transcript.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 + i * 0.2 }}
                    className={`flex ${m.who === 'rachel' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-[13.5px] leading-relaxed ${
                        m.who === 'rachel'
                          ? 'bg-[#F77F11]/15 border border-[#F77F11]/30 text-[#FFD9B3]'
                          : 'bg-white/[0.06] border border-white/10 text-neutral-200'
                      }`}
                    >
                      <p className={`text-[9.5px] tracking-[0.16em] uppercase font-bold mb-1 ${m.who === 'rachel' ? 'text-[#FFB672]' : 'text-neutral-500'}`}>
                        {m.who === 'rachel' ? 'Rachel' : es ? 'Cliente' : 'Guest'}
                      </p>
                      {m.msg}
                    </div>
                  </motion.div>
                ))}
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.95 }}
                className="mt-5 flex items-center gap-3 rounded-2xl bg-emerald-400/10 border border-emerald-400/25 px-4 py-3.5"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[13px] font-bold text-emerald-300">
                    {es ? 'Reserva confirmada — Laura · 4 personas · Sáb 7:45 PM' : 'Reservation confirmed — Laura · Party of 4 · Sat 7:45 PM'}
                  </p>
                  <p className="text-[11.5px] text-neutral-400">
                    {es ? 'Sincronizada con OpenTable · SMS de confirmación enviado' : 'Synced to OpenTable · Confirmation SMS sent'}
                  </p>
                </div>
              </motion.div>
            </div>
          </Grow>
        </div>

        {/* Capability tiles */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {caps.map((cp, i) => (
            <Grow key={cp.t} i={i * 0.6}>
              <div className="h-full rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:border-[#F77F11]/40 transition-colors">
                <span className="inline-flex w-10 h-10 rounded-xl bg-[#F77F11]/15 border border-[#F77F11]/25 items-center justify-center">
                  <cp.icon className="w-5 h-5 text-[#FFB672]" />
                </span>
                <h3 className="mt-4 font-bold text-[15px] tracking-[-0.005em]">{cp.t}</h3>
                <p className="mt-1.5 text-[12.5px] text-neutral-400 leading-relaxed">{cp.b}</p>
              </div>
            </Grow>
          ))}
        </div>

        {/* Stats strip */}
        <Grow i={2}>
          <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
            {stats.map((s) => (
              <div key={s.k} className="p-5 text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#F77F11] tracking-[-0.02em]">{s.k}</p>
                <p className="mt-1 text-[11.5px] text-neutral-400">{s.v}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#F77F11]/15 border border-[#F77F11]/40 text-[#ffb077] text-[11px] font-bold px-3 py-1.5">
            <Zap className="w-3.5 h-3.5" />
            {es
              ? 'Cada llamada perdida un viernes por la noche es una mesa que se va con tu competencia.'
              : 'Every missed Friday-night call is a table walking to your competitor.'}
          </p>
        </Grow>
      </div>
    </section>
  );
}
